/**
 * Wraps every /account page. A staff or clinic session that has not passed its second factor is
 * sent to finish signing in first. A signed-in person with no customer record, which is every staff
 * member and most clinic members, is sent to their own area: the admin app or the clinic portal.
 * Without this, the account pages asked for a formula that does not exist and reported a
 * connection problem. Signed-out visitors see the page, which offers sign-in itself.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { homePathFor, secondFactorPathFor } from "@lattice/core";
import { LinkButton } from "@lattice/ui/LinkButton";
import { loadSession } from "../api.ts";
import { AuthLayout, authStyles } from "../AuthLayout.tsx";
type Gate = "checking" | "open" | "no_record";
export function AccountGate({
  children
}: {
  readonly children: ReactNode;
}) {
  const [gate, setGate] = useState<Gate>("checking");
  useEffect(() => {
    void loadSession().then(loaded => {
      if (loaded.status !== "ok" || loaded.me.anonymous || loaded.me.customer) {
        setGate("open");
        return;
      }
      const here = window.location.pathname + window.location.search;
      const finish = secondFactorPathFor(loaded.me);
      if (finish !== null) {
        window.location.assign(`${finish}?next=${encodeURIComponent(here)}`);
        return;
      }
      const home = homePathFor(loaded.me);
      if (home === "/account") setGate("no_record");else window.location.replace(home);
    }, () => {
      /* No answer about who this is. The page behind asks for its own data and says if that fails. */
      setGate("open");
    });
  }, []);
  if (gate === "open") return children;
  if (gate === "checking") return <AuthLayout title="Opening your account" lead="One moment.">
        {null}
      </AuthLayout>;
  return <AuthLayout title="No formula yet" lead="This account has no labs or formula. Upload a lab report to start one.">
      <div {...stylex.props(authStyles.form)}>
        <LinkButton href="/start">Upload your labs</LinkButton>
      </div>
    </AuthLayout>;
}